/**
 * Phase 9 — Meta (Facebook) Pixel injection.
 *
 * Consent-gated : appelé seulement si `categories.marketing === true`.
 * Idle-deferred : passe par `idleInject` côté caller pour ne jamais bloquer
 * le critical path.
 *
 * Pixel ID Océanne — dataset « Océanne — AMG » (Business Manager Meta).
 */
import { idleInject } from '@/lib/perf/idle-inject';

const FB_PIXEL_ID_PLACEHOLDER = 'REPLACE_AT_END_FB_PIXEL_ID';
const FB_PIXEL_ID: string = '27048449291449455';

interface FbqGlobal {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: (...args: unknown[]) => void;
  loaded?: boolean;
  version?: string;
}

declare global {
  interface Window {
    fbq?: FbqGlobal;
    _fbq?: FbqGlobal;
  }
}

export function injectMetaPixel(pixelId: string = FB_PIXEL_ID): void {
  if (typeof window === 'undefined') return;
  if (pixelId === FB_PIXEL_ID_PLACEHOLDER) {
    if (import.meta.env.DEV) {
      console.warn('[meta-pixel] skipped: placeholder not replaced (Phase 10)');
    }
    return;
  }
  if (window.fbq) return; // already injected

  // Official Meta Pixel snippet (transposed inline to satisfy TS — same logic).
  const fbq: FbqGlobal = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue?.push(args);
    }
  } as FbqGlobal;
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(script, first);

  fbq('init', pixelId);
  fbq('track', 'PageView');
}

export function deferMetaPixel(): void {
  idleInject(() => injectMetaPixel());
}

/**
 * Événement de conversion « Lead » — à appeler sur soumission réussie du
 * formulaire. No-op si le pixel n'est pas chargé (= consentement marketing
 * refusé), ce qui respecte la Loi 25. C'est le signal que Meta optimise
 * pour le lieu de conversion « Site Web ».
 *
 * `eventId` : identifiant unique de la soumission, partagé avec l'event
 * serveur (CAPI) envoyé par le worker. Meta déduplique les deux events qui
 * portent le MÊME `eventID` → le pixel (navigateur, accepteurs cookies) et le
 * CAPI (serveur, TOUT le monde y compris refus-cookies) ne comptent qu'1 Lead.
 * Sans `eventID` partagé, pas de dédup → doublons sur les accepteurs.
 */
export function trackLead(eventId?: string): void {
  if (typeof window === 'undefined') return;
  if (!window.fbq) return; // pas de consentement marketing → pas de tracking
  if (eventId) {
    window.fbq('track', 'Lead', {}, { eventID: eventId });
  } else {
    window.fbq('track', 'Lead');
  }
}
